/**
 * When a vehicle is closed out, and why the answer depends on a date.
 *
 * Winnie, 18 September:
 *
 *   is_closed_out(vin):
 *     if Vehicle.reached_sold_delivered_at < gate_go_live_date:
 *       return payment_status == FullyPaid                                  // old rule
 *     else:
 *       return payment_status == FullyPaid AND ARRecord.ppsr_discharged     // new rule
 *
 * The grandfather half is the part worth being careful with. Turning the discharge
 * requirement on for everything would immediately un-settle vehicles that are already
 * finished — cars sold and paid for weeks ago, whose registrations may never have been
 * discharged through this platform because the automatic discharge did not exist when they
 * went through. Those would reappear on the chase list as unfinished business that nobody can
 * action, which is worse than the gap the gate exists to close.
 *
 * Two deliberate readings of the edge cases, both erring towards the old rule:
 *
 *   - A vehicle with no `reachedSoldDeliveredAt` recorded predates the stamp, so it predates
 *     the gate. Old rule.
 *   - A gate with no go-live date set is a gate that has not been turned on. Old rule for
 *     everything, which is the state this ships in: Winnie turns it on in Admin → Settings
 *     when she is ready, and only vehicles that reach sold-delivered after that moment are
 *     subject to it.
 *
 * No network, no database — the report, the AR table and the settlement write all call this
 * so they cannot reach different conclusions about the same car.
 */

export interface CloseOutInput {
  /** Is the money all in? Decided by settleGate in lib/arPayments — this takes its answer. */
  fullyPaid: boolean
  /** True when there was nothing to discharge, or the register confirmed the discharge. */
  ppsrDischarged: boolean
  /** When this vehicle first reached a sold-delivered state. */
  reachedSoldDeliveredAt?: string | null
  /** The date the discharge requirement starts applying. Null = not turned on. */
  gateGoLiveDate?: string | null
}

export interface CloseOutVerdict {
  closedOut: boolean
  /** Which rule was applied, so a screen can say why rather than just refusing. */
  rule: 'payment-only' | 'payment-and-discharge'
  reason: string
}

const day = (v?: string | null): string => String(v || '').slice(0, 10)

/**
 * Is this vehicle subject to the discharge requirement?
 *
 * Only when the gate is live AND the vehicle reached sold-delivered on or after the day it
 * went live. Same-day counts as subject to it: a gate that starts "sometime during Tuesday"
 * is a gate nobody can explain afterwards.
 */
export function dischargeGateApplies(
  reachedSoldDeliveredAt?: string | null,
  gateGoLiveDate?: string | null,
): boolean {
  const golive = day(gateGoLiveDate)
  if (!golive) return false
  const reached = day(reachedSoldDeliveredAt)
  if (!reached) return false
  return reached >= golive
}

export function closeOutVerdict(input: CloseOutInput): CloseOutVerdict {
  const applies = dischargeGateApplies(input.reachedSoldDeliveredAt, input.gateGoLiveDate)

  if (!applies) {
    return {
      closedOut: !!input.fullyPaid,
      rule: 'payment-only',
      reason: input.fullyPaid
        ? 'Paid in full.'
        : 'Not yet paid in full.',
    }
  }

  if (!input.fullyPaid) {
    return {
      closedOut: false,
      rule: 'payment-and-discharge',
      reason: 'Not yet paid in full.',
    }
  }

  if (!input.ppsrDischarged) {
    return {
      closedOut: false,
      rule: 'payment-and-discharge',
      reason:
        'Paid in full, but the PPSR registration against this vehicle has not been discharged. ' +
        'Retry the discharge from Reports → PPSR Register.',
    }
  }

  return {
    closedOut: true,
    rule: 'payment-and-discharge',
    reason: 'Paid in full and the PPSR registration is discharged.',
  }
}
